import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { CheckCircle2 as CheckCircle, X, Sparkles as Sparkle, Send as PaperPlaneTilt, Pencil, Copy, Image as ImageIcon, BarChart3, Upload, Trash2, Rocket, Brain, Calendar as CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import PublishingSetupWizard from "../components/PublishingSetupWizard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_TABS = [
  { id: "pending",   label: "Pending",   sub: "Needs your review" },
  { id: "approved",  label: "Approved",  sub: "Scheduled to ship" },
  { id: "published", label: "Published", sub: "Already live" },
];

export default function Approvals() {
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("all");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [posts, setPosts] = useState([]);
  const [active, setActive] = useState(null);
  const [notes, setNotes] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [viewMode, setViewMode] = useState("source"); // source | preview
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [autoPublishing, setAutoPublishing] = useState(false);
  const [knowledgeSuggestion, setKnowledgeSuggestion] = useState(null); // {post_id, brand_id, summary, raw}
  const [markPubModalFor, setMarkPubModalFor] = useState(null); // post object
  const [markPubDate, setMarkPubDate] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!active || viewMode !== "preview") return;
    setPreviewLoading(true);
    api.renderedHtml(active.post_id)
      .then((r) => setPreviewHtml(r.html || ""))
      .catch(() => setPreviewHtml(""))
      .finally(() => setPreviewLoading(false));
  }, [active?.post_id, viewMode, active?.title, active?.subtitle, active?.body_markdown, active?.cta, active?.cover_image_updated_at]);

  const load = async () => {
    const all = await api.listBrands();
    setBrands(all);
    let p;
    if (brandId === "all") {
      const arr = await Promise.all(all.map((b) => api.listContent(b.brand_id, statusFilter)));
      p = arr.flat();
    } else {
      p = await api.listContent(brandId, statusFilter);
    }
    // Sort: pending/approved by scheduled_date asc; published by published_at desc (latest first)
    if (statusFilter === "published") {
      p.sort((a, b) => (b.published_at || b.scheduled_date || "").localeCompare(a.published_at || a.scheduled_date || ""));
    } else {
      p.sort((a, b) => (a.scheduled_date || "").localeCompare(b.scheduled_date || ""));
    }
    setPosts(p);
  };
  useEffect(() => { load(); }, [brandId, statusFilter]);

  // Deep-link: /approvals?post=POST_ID opens that post in the drawer and switches
  // to its tab if needed. Used by the Calendar to navigate to any post.
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const pid = sp.get("post");
    if (!pid) return;
    (async () => {
      try {
        const post = await api.getPost(pid);
        if (post?.status && post.status !== statusFilter) setStatusFilter(post.status);
        setActive(post);
        setEditMode(false);
        // Strip the query param so a refresh doesn't keep re-opening
        navigate(`/approvals`, { replace: true });
      } catch (_) {
        toast.error("Couldn't load that post");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const approve = async (p, schedule_date) => {
    await api.approvePost(p.post_id, { schedule_date });
    toast.success("Approved");
    setActive(null); load();
  };
  const reject = async (p) => {
    if (!notes.trim()) return toast.error("Add improvement notes");
    toast.loading("Regenerating…", { id: "rev" });
    try {
      const result = await api.rejectPost(p.post_id, notes);
      toast.success("Rewritten with your notes", { id: "rev" });
      const rawNotes = notes;
      setNotes("");
      // If the backend flagged the notes as reusable brand knowledge, offer a one-tap promote.
      if (result?.knowledge_suggestion) {
        setKnowledgeSuggestion({
          post_id: p.post_id,
          brand_id: p.brand_id,
          summary: result.knowledge_suggestion.summary,
          raw: result.knowledge_suggestion.raw || rawNotes,
        });
      } else {
        setActive(null);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rewrite failed", { id: "rev" });
    }
  };

  const promoteSuggestion = async () => {
    if (!knowledgeSuggestion) return;
    try {
      await api.addKnowledgeFromSuggestion({
        brand_id: knowledgeSuggestion.brand_id,
        summary: knowledgeSuggestion.summary,
        raw: knowledgeSuggestion.raw,
        source: "reject_note",
      });
      toast.success("Added to brand knowledge — future posts will use this rule.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add to knowledge");
    } finally {
      setKnowledgeSuggestion(null);
      setActive(null);
    }
  };

  const removePost = async (p) => {
    if (!window.confirm(`Delete "${p.title || 'this post'}"? This cannot be undone.`)) return;
    try {
      await api.deletePost(p.post_id);
      toast.success("Deleted");
      if (active?.post_id === p.post_id) setActive(null);
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const openMarkPublished = (p) => {
    setMarkPubModalFor(p);
    // Default the picker to "now" but formatted for an HTML datetime-local input.
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    setMarkPubDate(`${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`);
  };
  const confirmMarkPublished = async () => {
    const p = markPubModalFor;
    if (!p) return;
    let iso = null;
    if (markPubDate) {
      const d = new Date(markPubDate);
      if (!isNaN(d.getTime())) iso = d.toISOString();
    }
    try {
      await api.markPublished(p.post_id, iso);
      toast.success(iso ? "Marked published at the date you set — calendar updated." : "Marked published");
      setMarkPubModalFor(null);
      setMarkPubDate("");
      setActive(null);
      load();
    } catch (e) {
      toast.error("Could not mark as published");
    }
  };
  const saveEdit = async (p, patch) => {
    await api.editPost(p.post_id, patch);
    toast.success("Saved");
    setEditMode(false);
    const fresh = await api.getPost(p.post_id);
    setActive(fresh);
    load();
  };
  const copyToSubstack = async (p) => {
    try {
      const { html } = await api.renderedHtml(p.post_id);
      // Build a plain-text fallback by stripping markdown asterisks/hashes
      const plain = `${p.title}\n\n${p.subtitle ? `${p.subtitle}\n\n` : ""}${(p.body_markdown || "")
        .replace(/^#+\s+/gm, "")
        .replace(/\*\*(.+?)\*\*/g, "$1")
        .replace(/\*(.+?)\*/g, "$1")
        .replace(/`(.+?)`/g, "$1")
        .replace(/\[(.+?)\]\((.+?)\)/g, "$1 ($2)")}\n\n${p.cta || ""}`;
      // Write HTML + plain text to clipboard — Substack's rich editor accepts text/html on paste.
      if (window.ClipboardItem) {
        const item = new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([plain], { type: "text/plain" }),
        });
        await navigator.clipboard.write([item]);
      } else {
        await navigator.clipboard.writeText(plain);
      }
      toast.success("Copied with formatting. Paste into Substack — bold/headings/links will render.");
    } catch (e) {
      toast.error("Copy failed — try the 'Draft to Substack' button instead.");
    }
  };
  const setTitle = async (p, newTitle) => {
    const fresh = await api.setPostTitle(p.post_id, newTitle);
    setActive(fresh);
    load();
    toast.success("Headline switched");
  };
  const draftToSubstack = async (p) => {
    toast.loading("Creating draft in Substack…", { id: "sub" });
    try {
      const r = await api.draftToSubstack(p.post_id);
      toast.success("Draft ready in Substack — click Publish there.", { id: "sub" });
      if (r.edit_url) window.open(r.edit_url, "_blank", "noopener");
      const fresh = await api.getPost(p.post_id);
      setActive(fresh);
      load();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || "Substack draft failed";
      if (status === 409) {
        toast.error("Your Substack session expired. Reconnect from Brand → Settings.", { id: "sub" });
      } else if (status === 400 && /not connected/i.test(detail)) {
        toast.error("Connect this brand to Substack first — Brand → Settings → Connect Substack.", { id: "sub" });
      } else {
        toast.error(detail, { id: "sub" });
      }
    }
  };
  const publishNow = async (p) => {
    toast.loading("Approving + drafting in Substack…", { id: "pn" });
    try {
      const r = await api.publishNow(p.post_id);
      toast.success("Approved · draft sitting in Substack", { id: "pn" });
      if (r.edit_url) window.open(r.edit_url, "_blank", "noopener");
      setActive(null); load();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || "Approve+draft failed";
      if (status === 409) toast.error("Substack session expired. Reconnect from Brand → Settings.", { id: "pn" });
      else if (status === 400 && /not connected/i.test(detail)) toast.error("Connect this brand to Substack first.", { id: "pn" });
      else toast.error(detail, { id: "pn" });
    }
  };
  const generateCover = async (p) => {
    toast.loading("Generating cover…", { id: "cv" });
    try {
      await api.generateCover(p.post_id);
      const fresh = await api.getPost(p.post_id);
      setActive(fresh);
      toast.success("Cover generated", { id: "cv" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed", { id: "cv" });
    }
  };
  const replaceCover = async (p, file) => {
    if (!file) return;
    toast.loading("Uploading…", { id: "cv" });
    try {
      await api.uploadCover(p.post_id, file);
      const fresh = await api.getPost(p.post_id);
      setActive(fresh);
      toast.success("Cover replaced", { id: "cv" });
    } catch (e) {
      toast.error("Upload failed", { id: "cv" });
    }
  };
  const removeCover = async (p) => {
    await api.deleteCover(p.post_id);
    const fresh = await api.getPost(p.post_id);
    setActive(fresh);
    toast.success("Cover removed");
  };
  const markPublishedNow = async (p) => {
    await api.markPublished(p.post_id, null);
    toast.success("Marked published");
    setActive(null); load();
  };

  const autoPublishAll = async (p) => {
    const b = brands.find((x) => x.brand_id === p.brand_id);
    // 1. Check Ayrshare is connected for the workspace
    setAutoPublishing(true);
    try {
      // Check status — if no Ayrshare key for this workspace OR no profile keys, open wizard
      const ws = b?.workspace_id;
      let status = null;
      try { status = await api.ayrshareStatus(ws); } catch (e) {}
      if (!status?.connected) {
        setAutoPublishing(false);
        setWizardOpen(true);
        return;
      }
      // 2. Call auto-publish-all
      toast.loading("Publishing everywhere…", { id: "auto" });
      const r = await api.autoPublishAll(p.post_id);
      const okCount = r.results.filter((x) => x.ok).length;
      const failCount = r.results.length - okCount;
      if (failCount === 0) toast.success(`Shipped to ${okCount} platform${okCount === 1 ? "" : "s"}`, { id: "auto" });
      else toast.warning(`Shipped to ${okCount}/${r.results.length}. Open the post to see what failed.`, { id: "auto" });
      const fresh = await api.getPost(p.post_id);
      setActive(fresh);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-publish failed", { id: "auto" });
    } finally {
      setAutoPublishing(false);
    }
  };

  return (
    <div data-testid="approvals-page" className="max-w-7xl">
      <div className="mb-8">
        <div className="text-eyebrow mb-3">Pipeline</div>
        <h1 className="font-display font-black text-5xl tracking-tighter">
          {statusFilter === "pending" ? "Approval Queue" : statusFilter === "approved" ? "Approved & Scheduled" : "Published"}
        </h1>
        <p className="text-[#52525B] mt-3">
          {statusFilter === "pending" && "Nothing publishes without your green light. Reject with notes and we rewrite instantly."}
          {statusFilter === "approved" && "Posts you've signed off on, waiting for their scheduled date or autopilot."}
          {statusFilter === "published" && "Everything that's already shipped — manual or auto."}
        </p>
      </div>

      <div className="flex border-b-2 border-black mb-6" data-testid="status-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => { setStatusFilter(t.id); setActive(null); }}
            data-testid={`status-tab-${t.id}`}
            className={`px-5 py-3 text-sm font-display font-bold border-r-2 border-black transition-colors ${
              statusFilter === t.id ? "bg-[#0A0A0B] text-white" : "bg-white text-[#0A0A0B] hover:bg-[#F4F5F6]"
            }`}
          >
            <div className="uppercase tracking-wider">{t.label}</div>
            <div className={`text-[10px] mono normal-case font-normal ${statusFilter === t.id ? "text-[#A1A1AA]" : "text-[#52525B]"}`}>{t.sub}</div>
          </button>
        ))}
      </div>

      <div className="flex gap-2 mb-6 items-center">
        <span className="text-eyebrow">Filter:</span>
        <select value={brandId} onChange={(e) => setBrandId(e.target.value)}
                data-testid="brand-filter-select"
                className="border border-[#E2E4E9] focus:border-black p-2 text-sm outline-none">
          <option value="all">All brands</option>
          {brands.map((b) => <option key={b.brand_id} value={b.brand_id}>{b.name}</option>)}
        </select>
        <span className="ml-auto text-xs mono text-[#A1A1AA]">{posts.length} {statusFilter} post{posts.length === 1 ? "" : "s"}</span>
      </div>

      {posts.length === 0 ? (
        <div className="border-2 border-dashed border-[#E2E4E9] p-16 text-center" data-testid="empty-state">
          <CheckCircle size={48} className="mx-auto text-[#A1A1AA]" />
          <p className="font-display font-bold text-2xl mt-4">
            {statusFilter === "pending" && "Nothing pending"}
            {statusFilter === "approved" && "No approved posts yet"}
            {statusFilter === "published" && "Nothing published yet"}
          </p>
          <p className="text-sm text-[#52525B] mt-2">
            {statusFilter === "pending" && "Generate posts from a brand to fill this queue."}
            {statusFilter === "approved" && "Approve a pending post to schedule it for autopilot."}
            {statusFilter === "published" && "Once a post ships, it'll land here."}
          </p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-0 border-l border-t border-[#E2E4E9]">
          {posts.map((p) => {
            const b = brands.find((x) => x.brand_id === p.brand_id);
            return (
              <div
                key={p.post_id}
                onClick={() => { setActive(p); setEditMode(false); }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { setActive(p); setEditMode(false); }}}
                data-testid={`post-card-${p.post_id}`}
                className="relative text-left p-6 border-r border-b border-[#E2E4E9] hover:bg-[#F4F5F6] transition-colors cursor-pointer focus:outline-none focus:bg-[#F4F5F6]"
              >
                <button
                  onClick={(e) => { e.stopPropagation(); removePost(p); }}
                  data-testid={`delete-post-${p.post_id}`}
                  aria-label="Delete post"
                  title="Delete post"
                  className="absolute top-3 right-3 p-1.5 text-[#A1A1AA] hover:text-[#E11D48] hover:bg-white"
                >
                  <Trash2 size={14} />
                </button>
                <div className="flex items-center justify-between mb-3 pr-8">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3" style={{ background: b?.color }} />
                    <span className="text-xs mono uppercase tracking-wider">{b?.name}</span>
                  </div>
                  <span className="text-xs mono text-[#A1A1AA]" title={p.status === "published" ? "Published" : "Scheduled"}>
                    {p.status === "published"
                      ? (p.published_at || p.scheduled_date || "").slice(0, 10)
                      : (p.scheduled_date || "").slice(0, 10)}
                  </span>
                </div>
                <div className="font-display font-bold text-xl tracking-tight line-clamp-2">{p.title}</div>
                {p.hook && <p className="text-sm text-[#52525B] mt-2 line-clamp-2">{p.hook}</p>}
                <div className="mt-4 flex items-center justify-between text-xs mono">
                  <span className="uppercase">{p.post_type}</span>
                  <span className="text-[#F59E0B]">viral {p.viral_score_prediction}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {active && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-stretch justify-end" onClick={() => setActive(null)}>
          <div className="bg-white w-full max-w-3xl h-full overflow-y-auto border-l-2 border-black" onClick={(e) => e.stopPropagation()} data-testid="post-detail-drawer">
            <div className="sticky top-0 bg-white border-b-2 border-black px-6 py-4 flex items-center justify-between z-10">
              <div className="text-eyebrow">Review draft</div>
              <button onClick={() => setActive(null)} data-testid="close-drawer"><X size={20} /></button>
            </div>
            <div className="p-8">
              {editMode ? (
                <EditForm post={active} onSave={saveEdit} onCancel={() => setEditMode(false)} />
              ) : (
                <>
                  <CoverImageBlock post={active} onGenerate={(opts) => generateCover(active, opts)} onReplace={(f) => replaceCover(active, f)} onRemove={() => removeCover(active)} />

                  {active.headline_variants?.length > 0 && (
                    <div className="mt-4 border border-[#E2E4E9] p-4 bg-[#F4F5F6]">
                      <div className="text-eyebrow mb-2">A/B headline variants — click to make primary</div>
                      <div className="space-y-2">
                        {active.headline_variants.map((h, i) => (
                          <button key={i} onClick={() => setTitle(active, h)}
                                  data-testid={`headline-variant-${i}`}
                                  className={`w-full text-left p-2 border ${h === active.title ? "border-[#0A0A0B] bg-white" : "border-transparent hover:bg-white"}`}>
                            <span className="text-xs mono text-[#A1A1AA] mr-2">V{i + 1}</span>
                            <span className="text-sm">{h}</span>
                            {h === active.title && <span className="text-xs mono text-[#059669] ml-2">ACTIVE</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <SubjectVariantsPanel active={active} onSubjectChange={(s) => setTitle(active, s)}/>

                  <div className="mt-6 flex items-center gap-2 border-b border-[#E2E4E9]">
                    <button
                      onClick={() => setViewMode("source")}
                      data-testid="view-source-tab"
                      className={`px-4 py-2 text-sm -mb-px border-b-2 ${viewMode === "source" ? "border-[#0A0A0B] font-semibold" : "border-transparent text-[#A1A1AA] hover:text-[#0A0A0B]"}`}
                    >
                      Source
                    </button>
                    <button
                      onClick={() => setViewMode("preview")}
                      data-testid="view-preview-tab"
                      className={`px-4 py-2 text-sm -mb-px border-b-2 ${viewMode === "preview" ? "border-[#0A0A0B] font-semibold" : "border-transparent text-[#A1A1AA] hover:text-[#0A0A0B]"}`}
                    >
                      Substack preview
                    </button>
                  </div>

                  {viewMode === "preview" ? (
                    <div className="mt-6" data-testid="substack-preview">
                      <div className="text-xs mono text-[#A1A1AA] uppercase mb-2">Renders exactly as Substack will display it</div>
                      <div className="border border-[#E2E4E9] bg-[#FAFAFA] p-1">
                        {previewLoading ? (
                          <div className="p-8 text-center text-sm text-[#A1A1AA]">Loading preview…</div>
                        ) : (
                          <iframe
                            title="Substack preview"
                            sandbox=""
                            srcDoc={previewHtml}
                            data-testid="substack-preview-iframe"
                            className="w-full bg-white"
                            style={{ minHeight: "70vh", border: "none" }}
                          />
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      <h2 className="font-display font-black text-4xl tracking-tighter leading-tight mt-6">{active.title}</h2>
                      {active.subtitle && <p className="text-lg text-[#52525B] mt-2 italic">{active.subtitle}</p>}
                      <div className="flex items-center gap-3 mt-4 mono text-xs text-[#A1A1AA]">
                        <span className="uppercase">{active.post_type}</span>
                        <span>·</span>
                        <span className="text-[#F59E0B]">viral score {active.viral_score_prediction}</span>
                        <span>·</span>
                        <span>{(active.scheduled_date || "").slice(0, 10)}</span>
                      </div>
                      {active.hook && (
                        <div className="mt-6 border-l-4 border-[#002FA7] pl-4">
                          <div className="text-eyebrow mb-1">Hook</div>
                          <p className="font-display font-semibold text-xl">{active.hook}</p>
                        </div>
                      )}
                      <div className="prose prose-neutral max-w-none mt-8 whitespace-pre-wrap text-base leading-relaxed">
                        {active.body_markdown}
                      </div>
                      {active.cta && (
                        <div className="mt-6 border border-[#0A0A0B] bg-[#F4F5F6] p-4">
                          <div className="text-eyebrow mb-1">CTA</div>
                          <p className="font-semibold">{active.cta}</p>
                        </div>
                      )}
                    </>
                  )}
                  {active.revision_notes?.length > 0 && (
                    <div className="mt-8">
                      <div className="text-eyebrow mb-2">Revision history</div>
                      <div className="space-y-2">
                        {active.revision_notes.map((r, i) => (
                          <div key={i} className="border border-[#E2E4E9] p-3 text-sm">
                            <span className="text-xs mono text-[#A1A1AA]">{(r.at || "").slice(0, 16)}</span>
                            <p className="mt-1">{r.note}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <AnalyticsBlock post={active} />
                </>
              )}
            </div>

            {!editMode && (
              <div className="sticky bottom-0 bg-white border-t-2 border-black p-6 space-y-3">
                {active.status === "pending" && (
                  <textarea
                    placeholder="Improvement notes — we'll rewrite the post applying your edits…"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    data-testid="improvement-notes-input"
                    rows={2}
                    className="w-full border border-[#E2E4E9] focus:border-black p-3 outline-none resize-none text-sm"
                  />
                )}
                {active.status === "approved" && (
                  <div className="text-xs mono text-[#A1A1AA] uppercase">
                    Approved {(active.approved_at || "").slice(0, 16)} · scheduled {(active.scheduled_date || "").slice(0, 10)}
                  </div>
                )}
                {active.status === "published" && (
                  <div className="text-xs mono text-[#A1A1AA] uppercase">
                    Published {(active.published_at || "").slice(0, 16)} · via {active.delivery_method || "—"}
                    {active.substack_edit_url && (
                      <a href={active.substack_edit_url} target="_blank" rel="noreferrer" className="ml-2 underline">open in Substack</a>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap gap-2 justify-between">
                  <div className="flex gap-2">
                    <button onClick={() => setEditMode(true)} data-testid="edit-post-button" className="px-4 py-2 text-sm border border-black hover:bg-[#F4F5F6] flex items-center gap-2">
                      <Pencil size={14} /> Edit
                    </button>
                    {active.status === "pending" && (
                      <button onClick={() => reject(active)} data-testid="reject-post-button" className="px-4 py-2 text-sm border border-[#E11D48] text-[#E11D48] hover:bg-[#E11D48] hover:text-white flex items-center gap-2">
                        <Sparkle size={14} /> Rewrite with notes
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => copyToSubstack(active)} data-testid="copy-substack-button" className="px-4 py-2 text-sm border border-black hover:bg-[#F4F5F6] flex items-center gap-2">
                      <Copy size={14} /> Copy for Substack
                    </button>
                    {active.status !== "published" && (
                      <button onClick={() => draftToSubstack(active)} data-testid="draft-substack-button" className="px-4 py-2 text-sm border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white flex items-center gap-2">
                        <PaperPlaneTilt size={14} /> Draft to Substack
                      </button>
                    )}
                    {active.status === "pending" && (
                      <button onClick={() => publishNow(active)} data-testid="publish-now-button" className="px-4 py-2 text-sm bg-[#002FA7] text-white hover:bg-[#00227A] flex items-center gap-2">
                        <PaperPlaneTilt size={14} /> Approve + Draft
                      </button>
                    )}
                    {active.status !== "published" && (
                      <button onClick={() => autoPublishAll(active)} disabled={autoPublishing} data-testid="auto-publish-all-button" className="px-4 py-2 text-sm bg-[#0A0A0B] text-white hover:bg-[#002FA7] disabled:opacity-50 flex items-center gap-2">
                        <Rocket size={14} /> {autoPublishing ? "Shipping…" : "Auto-publish to ALL"}
                      </button>
                    )}
                    {active.status === "pending" && (
                      <button onClick={() => approve(active)} data-testid="approve-post-button" className="px-4 py-2 text-sm bg-[#059669] text-white hover:bg-[#047857] flex items-center gap-2">
                        <CheckCircle size={14} /> Approve & schedule
                      </button>
                    )}
                    {active.status !== "published" && (
                      <button onClick={() => openMarkPublished(active)} data-testid="mark-published-button" className="px-4 py-2 text-sm bg-[#0A0A0B] text-white hover:bg-[#002FA7] flex items-center gap-2">
                        <CalendarIcon size={14} /> Mark published…
                      </button>
                    )}
                    <button onClick={() => removePost(active)} data-testid="drawer-delete-post-button" className="px-4 py-2 text-sm border border-[#E11D48] text-[#E11D48] hover:bg-[#E11D48] hover:text-white flex items-center gap-2 ml-auto">
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {wizardOpen && active && (
        <PublishingSetupWizard
          workspaceId={brands.find((b) => b.brand_id === active.brand_id)?.workspace_id}
          brandId={active.brand_id}
          onClose={() => setWizardOpen(false)}
          onConnected={() => { setWizardOpen(false); toast.success("Ready — try Auto-publish again"); }}
        />
      )}

      {knowledgeSuggestion && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
             data-testid="knowledge-suggestion-modal"
             onClick={() => setKnowledgeSuggestion(null)}>
          <div className="bg-white border-2 border-black w-full max-w-lg p-6 brutal-shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-eyebrow text-[#A1A1AA] flex items-center gap-2"><Brain size={14} /> Add to brand knowledge?</div>
              <button onClick={() => setKnowledgeSuggestion(null)} aria-label="Dismiss" data-testid="knowledge-suggestion-dismiss"><X size={18} /></button>
            </div>
            <h3 className="font-display font-black text-2xl tracking-tighter mb-3">This note looks reusable</h3>
            <p className="text-sm text-[#52525B] mb-3">
              Looks like more than a one-off fix — saving it means the AI applies it to every future post for this brand.
            </p>
            <div className="border border-[#E2E4E9] bg-[#F4F5F6] p-3 text-sm mb-4">
              <div className="text-xs mono uppercase text-[#A1A1AA] mb-1">Summary</div>
              <div className="font-semibold">{knowledgeSuggestion.summary}</div>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setKnowledgeSuggestion(null); setActive(null); }}
                      className="px-4 py-2 text-sm border border-black hover:bg-[#F4F5F6]"
                      data-testid="knowledge-suggestion-no">
                No, just for this post
              </button>
              <button onClick={promoteSuggestion}
                      data-testid="knowledge-suggestion-yes"
                      className="px-5 py-3 text-sm bg-[#0A0A0B] text-white hover:bg-[#002FA7]">
                Add to brand knowledge
              </button>
            </div>
          </div>
        </div>
      )}

      {markPubModalFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
             data-testid="mark-published-modal"
             onClick={() => setMarkPubModalFor(null)}>
          <div className="bg-white border-2 border-black w-full max-w-md p-6 brutal-shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-eyebrow text-[#A1A1AA]">Manual publish</div>
              <button onClick={() => setMarkPubModalFor(null)} aria-label="Close"><X size={18} /></button>
            </div>
            <h3 className="font-display font-black text-2xl tracking-tighter mb-3">When did it go live?</h3>
            <p className="text-sm text-[#52525B] mb-3">
              Use this if you published manually outside Vitality. We'll plot it on the calendar at that date.
            </p>
            <label className="text-eyebrow block mb-2">Published at</label>
            <input
              type="datetime-local"
              value={markPubDate}
              onChange={(e) => setMarkPubDate(e.target.value)}
              data-testid="mark-published-datetime"
              className="w-full border border-[#E2E4E9] focus:border-black p-3 outline-none"
            />
            <div className="flex gap-3 justify-end mt-5">
              <button onClick={() => setMarkPubModalFor(null)} className="px-4 py-2 text-sm border border-black hover:bg-[#F4F5F6]">Cancel</button>
              <button onClick={() => markPublishedNow(markPubModalFor)} data-testid="mark-published-now-btn"
                      className="px-4 py-2 text-sm border border-black hover:bg-[#F4F5F6]">Use now</button>
              <button onClick={confirmMarkPublished} data-testid="mark-published-confirm-btn"
                      className="px-5 py-3 text-sm bg-[#0A0A0B] text-white hover:bg-[#002FA7]">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EditForm({ post, onSave, onCancel }) {
  const [data, setData] = useState({
    title: post.title || "",
    subtitle: post.subtitle || "",
    body_markdown: post.body_markdown || "",
    cta: post.cta || "",
    scheduled_date: (post.scheduled_date || "").slice(0, 10),
  });
  return (
    <div>
      <div className="text-eyebrow mb-4">Edit draft</div>
      <div className="space-y-4">
        <input value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} className="w-full border border-[#E2E4E9] focus:border-black p-3 outline-none font-display font-bold text-2xl" />
        <input value={data.subtitle} onChange={(e) => setData({ ...data, subtitle: e.target.value })} className="w-full border border-[#E2E4E9] focus:border-black p-3 outline-none" />
        <textarea value={data.body_markdown} onChange={(e) => setData({ ...data, body_markdown: e.target.value })} rows={18} className="w-full border border-[#E2E4E9] focus:border-black p-3 outline-none text-sm font-mono" />
        <input value={data.cta} onChange={(e) => setData({ ...data, cta: e.target.value })} placeholder="CTA" className="w-full border border-[#E2E4E9] focus:border-black p-3 outline-none" />
        <input type="date" value={data.scheduled_date} onChange={(e) => setData({ ...data, scheduled_date: e.target.value })} className="border border-[#E2E4E9] focus:border-black p-3 outline-none" />
      </div>
      <div className="mt-6 flex gap-2 justify-end">
        <button onClick={onCancel} className="px-5 py-3 text-sm border border-black">Cancel</button>
        <button onClick={() => onSave(post, { ...data, scheduled_date: data.scheduled_date ? new Date(data.scheduled_date).toISOString() : undefined })}
                data-testid="save-edit-button"
                className="px-5 py-3 text-sm bg-[#0A0A0B] text-white">Save</button>
      </div>
    </div>
  );
}

function CoverImageBlock({ post, onGenerate, onReplace, onRemove }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [style, setStyle] = useState(post.cover_image_style || "editorial");
  const [customPrompt, setCustomPrompt] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [styles, setStyles] = useState([]);

  useEffect(() => { api.listCoverStyles().then(setStyles).catch(() => {}); }, []);
  useEffect(() => { setStyle(post.cover_image_style || "editorial"); }, [post.post_id, post.cover_image_style]);

  const url = post.cover_image_path
    ? `${BACKEND_URL}/api/files/cover/${post.post_id}?v=${encodeURIComponent(post.cover_image_updated_at || "")}`
    : null;

  const doGenerate = async () => {
    setBusy(true);
    await onGenerate({ style, prompt: customPrompt.trim() || undefined });
    setBusy(false);
  };
  const doReplace = async (e) => {
    setBusy(true);
    await onReplace(e.target.files?.[0]);
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="mt-6 border border-[#E2E4E9] p-4" data-testid="cover-image-block">
      <div className="flex items-center justify-between mb-3">
        <div className="text-eyebrow">Cover image</div>
        <div className="text-xs mono text-[#A1A1AA] uppercase">{post.cover_image_source || "none"}{post.cover_image_style ? ` · ${post.cover_image_style}` : ""}</div>
      </div>
      {url ? (
        <img src={url} alt="cover" className="w-full aspect-[3/1] object-cover border border-[#E2E4E9]" data-testid="cover-image-preview" />
      ) : (
        <div className="w-full aspect-[3/1] bg-[#F4F5F6] border border-dashed border-[#E2E4E9] flex items-center justify-center text-sm text-[#A1A1AA]">
          No cover yet
        </div>
      )}

      <div className="mt-3">
        <div className="text-xs mono text-[#A1A1AA] uppercase mb-2">Art direction style — Claude writes a viral prompt tuned to your brand audience, then Nano Banana renders</div>
        <div className="flex flex-wrap gap-1">
          {styles.map((s) => (
            <button
              key={s.id}
              onClick={() => setStyle(s.id)}
              data-testid={`style-${s.id}`}
              className={`px-3 py-1.5 text-xs border ${style === s.id ? "bg-[#0A0A0B] text-white border-[#0A0A0B]" : "bg-white text-[#0A0A0B] border-[#E2E4E9] hover:border-black"}`}
              title={s.desc}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-3">
        <button onClick={() => setShowAdvanced(!showAdvanced)} className="text-xs mono text-[#52525B] hover:text-black uppercase">
          {showAdvanced ? "Hide" : "Custom prompt override ▾"}
        </button>
        {showAdvanced && (
          <textarea
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            placeholder="Optional — describe the exact image you want. Leave blank to let Claude art-direct based on your brand audience."
            rows={3}
            data-testid="cover-custom-prompt"
            className="mt-2 w-full border border-[#E2E4E9] focus:border-black p-2 text-sm outline-none resize-none"
          />
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <input type="file" hidden ref={fileRef} accept="image/png,image/jpeg,image/webp" onChange={doReplace} data-testid="cover-upload-input" />
        <button onClick={doGenerate} disabled={busy} data-testid="generate-cover-button" className="px-3 py-2 text-xs bg-[#0A0A0B] text-white hover:bg-[#002FA7] disabled:opacity-50 flex items-center gap-1">
          <Sparkle size={12} /> {busy ? "Generating…" : (url ? `Regenerate (${style})` : `Generate cover (${style})`)}
        </button>
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="upload-cover-button" className="px-3 py-2 text-xs border border-black hover:bg-[#F4F5F6] flex items-center gap-1">
          <Upload size={12} /> Replace with upload
        </button>
        {url && (
          <button onClick={onRemove} disabled={busy} data-testid="remove-cover-button" className="px-3 py-2 text-xs border border-[#E11D48] text-[#E11D48] hover:bg-[#E11D48] hover:text-white flex items-center gap-1">
            <Trash2 size={12} /> Remove
          </button>
        )}
      </div>

      {post.cover_image_brief && (
        <details className="mt-3 text-xs text-[#52525B]">
          <summary className="cursor-pointer mono uppercase text-[#A1A1AA]">View brief used</summary>
          <p className="mt-2 p-2 bg-[#F4F5F6] whitespace-pre-wrap font-mono leading-relaxed">{post.cover_image_brief}</p>
        </details>
      )}
    </div>
  );
}

function AnalyticsBlock({ post }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ open_rate: "", click_rate: "", replies: "", shares: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.listAnalytics(post.post_id).then(setList);
  useEffect(() => { load(); }, [post.post_id]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.addAnalytics(post.post_id, {
        open_rate: form.open_rate ? parseFloat(form.open_rate) : undefined,
        click_rate: form.click_rate ? parseFloat(form.click_rate) : undefined,
        replies: form.replies ? parseInt(form.replies) : undefined,
        shares: form.shares ? parseInt(form.shares) : undefined,
        note: form.note || "",
      });
      setForm({ open_rate: "", click_rate: "", replies: "", shares: "", note: "" });
      load();
      toast.success("Analytics recorded — feeds the writer");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8" data-testid="analytics-block">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 size={16} />
        <span className="text-eyebrow">Post analytics</span>
      </div>
      <p className="text-xs text-[#52525B] mb-3">Drop in numbers from your Substack stats. We'll feed top-performers to future generations.</p>
      <div className="grid grid-cols-2 gap-2">
        <input placeholder="Open rate (e.g. 42.5)" value={form.open_rate} onChange={(e) => setForm({ ...form, open_rate: e.target.value })} data-testid="open-rate-input" className="border border-[#E2E4E9] focus:border-black p-2 text-sm outline-none" />
        <input placeholder="Click rate (e.g. 5.2)" value={form.click_rate} onChange={(e) => setForm({ ...form, click_rate: e.target.value })} data-testid="click-rate-input" className="border border-[#E2E4E9] focus:border-black p-2 text-sm outline-none" />
        <input placeholder="Replies" value={form.replies} onChange={(e) => setForm({ ...form, replies: e.target.value })} data-testid="replies-input" className="border border-[#E2E4E9] focus:border-black p-2 text-sm outline-none" />
        <input placeholder="Shares" value={form.shares} onChange={(e) => setForm({ ...form, shares: e.target.value })} data-testid="shares-input" className="border border-[#E2E4E9] focus:border-black p-2 text-sm outline-none" />
      </div>
      <textarea placeholder="Notes (what worked / didn't)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="analytics-note-input" rows={2} className="mt-2 w-full border border-[#E2E4E9] focus:border-black p-2 text-sm outline-none resize-none" />
      <button onClick={submit} disabled={busy} data-testid="save-analytics-button" className="mt-2 px-4 py-2 text-sm bg-[#0A0A0B] text-white hover:bg-[#002FA7]">
        {busy ? "Saving…" : "Record analytics"}
      </button>
      {list.length > 0 && (
        <div className="mt-4 space-y-2">
          {list.map((a) => (
            <div key={a.analytics_id} className="border border-[#E2E4E9] p-3 text-xs mono">
              <span className="text-[#A1A1AA]">{(a.created_at || "").slice(0, 10)}</span> · open: {a.open_rate ?? "—"} · clicks: {a.click_rate ?? "—"} · replies: {a.replies ?? "—"} · shares: {a.shares ?? "—"}
              {a.note && <div className="text-sm font-sans mt-1">{a.note}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SubjectVariantsPanel({ active, onSubjectChange }) {
  const [variants, setVariants] = React.useState(active.subject_variants || []);
  const [loading, setLoading] = React.useState(false);
  const [showABSplit, setShowABSplit] = React.useState(false);
  const [abStatus, setAbStatus] = React.useState(null);

  React.useEffect(() => {
    setVariants(active.subject_variants || []);
    api.abStatus(active.post_id).then((s) => setAbStatus(s.exists ? s : null)).catch(() => setAbStatus(null));
  }, [active.post_id, active.subject_variants]);

  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.subjectVariants(active.post_id);
      setVariants(r.variants || []);
    } catch (e) {
      // hard-gate already surfaces toast
    } finally { setLoading(false); }
  };

  const pick = async (s) => {
    await api.updatePostSubject(active.post_id, s);
    onSubjectChange(s);
  };

  return (
    <div className="mt-4 border border-[#E2E4E9] p-4 bg-[#FFFBEB]" data-testid="subject-variants-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-eyebrow">AI subject-line A/B variants</div>
        <div className="flex gap-2">
          <button onClick={generate} disabled={loading} data-testid="generate-subjects-button" className="text-eyebrow hover:text-black">
            {loading ? "Thinking…" : variants.length ? "Regenerate" : "Generate 3"}
          </button>
          {variants.length >= 2 && (
            <button onClick={() => setShowABSplit(true)} data-testid="ab-split-button" className="text-eyebrow hover:text-black border-l border-[#E2E4E9] pl-2">
              Split-send →
            </button>
          )}
        </div>
      </div>
      {variants.length === 0 ? (
        <div className="text-xs text-[#52525B]">3 alt subjects with predicted open-rate signals. Pick the winner — or split-test to your list.</div>
      ) : (
        <div className="space-y-2">
          {variants.map((v, i) => (
            <button key={i} onClick={() => pick(v.subject)} data-testid={`subject-variant-${i}`}
                    className={`w-full text-left p-2 border ${v.subject === active.title ? "border-black bg-white" : "border-transparent hover:bg-white"}`}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-medium">{v.subject}</span>
                <span className="text-xs mono text-[#A1A1AA]">{v.style} · {v.score}/10</span>
              </div>
              {v.why && <div className="text-xs text-[#52525B] mt-1">{v.why}</div>}
              {v.subject === active.title && <div className="text-xs mono text-[#059669] mt-1">ACTIVE</div>}
            </button>
          ))}
        </div>
      )}

      {abStatus && (
        <div className="mt-3 border border-black bg-white p-3" data-testid="ab-status-panel">
          <div className="text-eyebrow mb-1">Live A/B test</div>
          <div className="text-xs text-[#52525B] grid grid-cols-2 gap-2">
            <div><strong>A:</strong> {abStatus.v1_subject} → {abStatus.v1_opens} opens</div>
            <div><strong>B:</strong> {abStatus.v2_subject} → {abStatus.v2_opens} opens</div>
          </div>
          {abStatus.winner_sent ? (
            <div className="text-xs text-[#059669] mt-2 mono">Winner ({abStatus.winner_variant}): {abStatus.winner_subject} → sent to {abStatus.final_recipients?.length || 0}</div>
          ) : (
            <div className="text-xs text-[#A1A1AA] mt-2 mono">Winner sends after {abStatus.winner_send_after?.slice(11, 16)} UTC</div>
          )}
        </div>
      )}

      {showABSplit && (
        <ABSplitModal post={active} variants={variants} onClose={() => setShowABSplit(false)} onLaunched={() => { setShowABSplit(false); api.abStatus(active.post_id).then((s) => setAbStatus(s.exists ? s : null)); }}/>
      )}
    </div>
  );
}

function ABSplitModal({ post, variants, onClose, onLaunched }) {
  const [sample, setSample] = React.useState("");
  const [final, setFinal] = React.useState("");
  const [waitHours, setWaitHours] = React.useState(2);
  const [busy, setBusy] = React.useState(false);

  const parseEmails = (s) => s.split(/[\s,;\n]+/).map((x) => x.trim()).filter((x) => x.includes("@"));

  const launch = async () => {
    const sampleList = parseEmails(sample);
    const finalList = parseEmails(final);
    if (sampleList.length < 2) { toast.error("Sample list needs at least 2 emails (1 per variant)"); return; }
    if (finalList.length < 1) { toast.error("Final list needs at least 1 email"); return; }
    setBusy(true);
    try {
      await api.abSplitSend(post.post_id, {
        sample_recipients: sampleList,
        final_recipients: finalList,
        pick_winner_after_hours: parseInt(waitHours, 10),
      });
      toast.success(`A/B test launched. Winner sends in ${waitHours}h to ${finalList.length} recipients.`);
      onLaunched();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="ab-split-modal">
      <div className="bg-white border-2 border-black max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-black flex items-center justify-between bg-[#FAFAFA]">
          <div className="text-eyebrow">A/B subject split-send via Resend</div>
          <button onClick={onClose} className="text-eyebrow hover:text-[#E11D48]">×</button>
        </div>
        <div className="p-6 space-y-4">
          <h3 className="font-display font-black tracking-tighter text-2xl">Test 2 subjects. Winner auto-sends.</h3>
          <p className="text-xs text-[#52525B] leading-relaxed">Top-2 variants (by predicted score) ship to your sample list, evenly split. Wait window. Winner ships to the remaining list.</p>

          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="border border-black p-2 mono"><strong>A:</strong> {variants[0]?.subject}</div>
            <div className="border border-black p-2 mono"><strong>B:</strong> {variants[1]?.subject}</div>
          </div>

          <div>
            <label className="text-eyebrow mb-1 block">Sample list (10% of your audience — emails separated by commas / newlines)</label>
            <textarea value={sample} onChange={(e) => setSample(e.target.value)} rows={3} placeholder="sample1@a.com, sample2@b.com…" data-testid="ab-sample-input" className="w-full border-2 border-black p-2 text-sm font-mono"/>
          </div>
          <div>
            <label className="text-eyebrow mb-1 block">Final list (remaining 90% — winner sends here)</label>
            <textarea value={final} onChange={(e) => setFinal(e.target.value)} rows={4} placeholder="rest1@a.com, rest2@b.com…" data-testid="ab-final-input" className="w-full border-2 border-black p-2 text-sm font-mono"/>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-eyebrow">Wait window:</span>
            <input type="number" min={1} max={24} value={waitHours} onChange={(e) => setWaitHours(e.target.value)} data-testid="ab-wait-input" className="border-2 border-black p-1 text-sm w-16 mono"/>
            <span className="text-xs text-[#52525B]">hours</span>
          </div>
        </div>
        <div className="p-5 border-t border-black bg-[#FAFAFA] flex justify-end gap-2">
          <button onClick={onClose} className="text-eyebrow px-3 py-1.5 hover:text-black">Cancel</button>
          <button onClick={launch} disabled={busy} data-testid="ab-launch-button" className="bg-black text-white px-5 py-2 text-sm hover:bg-[#002FA7] disabled:opacity-50">
            {busy ? "Sending sample…" : "Launch A/B test"}
          </button>
        </div>
      </div>
    </div>
  );
}
